import React, { useState, useEffect } from 'react';

/**
 * Database Test Harness
 *
 * This page tests the D1 database connection by:
 * 1. Fetching domains data from /api/domains
 * 2. Displaying the results in a simple table
 *
 * This validates:
 * - Cloudflare Functions API endpoint works
 * - D1 database connection is configured correctly
 * - Data can be queried and returned as JSON
 */

export default function DatabaseTest() {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchDomains() {
      try {
        setLoading(true);

        // Call the Cloudflare Functions API endpoint
        const response = await fetch('/api/domains');

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const json = await response.json();

        if (json.success) {
          setDomains(json.data);
          setError(null);
        } else {
          throw new Error(json.error || 'Unknown error');
        }
      } catch (err) {
        console.error('Error fetching domains:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    fetchDomains();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Database Test Harness
          </h1>
          <p className="text-gray-600">
            Testing D1 database connection via Cloudflare Functions API
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-lg shadow-sm p-8">
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <span className="ml-4 text-gray-600">Loading domains...</span>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
            <h2 className="text-xl font-bold text-red-900 mb-2">Error</h2>
            <p className="text-red-700">{error}</p>
            <details className="mt-4">
              <summary className="cursor-pointer text-sm text-red-600 hover:text-red-800">
                Troubleshooting Tips
              </summary>
              <ul className="mt-2 text-sm text-gray-700 list-disc list-inside space-y-1">
                <li>Ensure wrangler.toml is configured with D1 binding</li>
                <li>Verify the database name matches in wrangler.toml</li>
                <li>Check that schema.sql has been executed in D1</li>
                <li>Run: wrangler d1 execute [DB_NAME] --local --file=database/schema.sql</li>
              </ul>
            </details>
          </div>
        )}

        {/* Success State */}
        {!loading && !error && domains.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="px-8 py-6 border-b border-gray-200 bg-green-50">
              <h2 className="text-xl font-bold text-green-900">
                ✅ Success! Database connection working
              </h2>
              <p className="text-green-700 text-sm mt-1">
                Found {domains.length} domain{domains.length !== 1 ? 's' : ''} in the database
              </p>
            </div>

            {/* Domains Table */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Emoji
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Focus
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {domains.map((domain) => (
                    <tr key={domain.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {domain.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-2xl">
                        {domain.emoji}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                        {domain.name}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {domain.description}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {domain.focus}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Raw JSON Display */}
            <details className="px-8 py-6 border-t border-gray-200 bg-gray-50">
              <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900">
                View Raw JSON Response
              </summary>
              <pre className="mt-4 p-4 bg-gray-900 text-green-400 rounded text-xs overflow-x-auto">
                {JSON.stringify(domains, null, 2)}
              </pre>
            </details>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && domains.length === 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <h2 className="text-xl font-bold text-yellow-900 mb-2">No Data Found</h2>
            <p className="text-yellow-700">
              The database connection works, but no domains were returned.
            </p>
            <p className="text-sm text-yellow-600 mt-2">
              Make sure you've imported the CSV data into your D1 database.
            </p>
          </div>
        )}

        {/* Footer Info */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-sm font-semibold text-blue-900 mb-2">How This Works</h3>
          <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
            <li>React app sends GET request to <code className="bg-blue-100 px-1 rounded">/api/domains</code></li>
            <li>Cloudflare Pages Function handles the request</li>
            <li>Function queries D1 database: <code className="bg-blue-100 px-1 rounded">SELECT * FROM domains</code></li>
            <li>Results returned as JSON to the React app</li>
            <li>React app displays the data in this table</li>
          </ol>
        </div>
      </div>
    </div>
  );
}
